import { createSelector } from '@ngrx/store';

import {
  AsyncJobFinishedInfo,
  AsyncJobPendingInfo,
  AsyncJobStartInfo,
  AsyncJobType,
} from '@recruitee/account-data';
import { createEntityCollectionStore, pipeState } from '@recruitee/ngrx';

import { AsyncJob, AsyncJobStatus } from '../async-jobs.types';

const idSelector = ({ uuid }: { uuid: string }) => uuid;
const { actions, namespace, reducer, selectors } = createEntityCollectionStore<AsyncJob>()(
  'AsyncJobState',
  {
    idSelector,
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => ({
      create: (state, payload: { job: { uuid: string; type: AsyncJobType } }) =>
        adapter.writeOne(state, {
          ...payload.job,
          status: AsyncJobStatus.IDLE,
          count: 0,
          total: 0,
          errors: [],
        }),
      start: (state, payload: { job: AsyncJobStartInfo }) =>
        adapter.upsertOne(
          state,
          {
            ...payload.job,
            count: 0,
            errors: [],
            status: AsyncJobStatus.START,
          },
          { scope: payload.job.type },
        ),
      pending: (state, payload: { job: AsyncJobPendingInfo }) =>
        adapter.upsertOne(
          state,
          {
            ...state.entityMap[payload.job.uuid]!,
            ...payload.job,
            status: AsyncJobStatus.WORKING,
          },
          { scope: payload.job.type },
        ),
      finish: (state, payload: { job: AsyncJobFinishedInfo }) => {
        const { errors, type: scope, uuid } = payload.job;
        const entity = {
          ...state.entityMap[uuid]!,
          ...payload.job,
          status: AsyncJobStatus.FINISHED,
        };

        return pipeState(
          state,
          next => adapter.upsertOne(next, entity),
          next =>
            errors && errors.length
              ? adapter.setError(next, errors, {
                  scope,
                  entities: [idSelector(payload.job)],
                })
              : next,
        );
      },

      clearFinished: state => {
        const ids = Object.values(state.entityMap)
          .filter(job => job && job.status === AsyncJobStatus.FINISHED)
          .map(job => idSelector(job!));
        return adapter.removeMany(state, ids);
      },
    }),
  },
);

const AsyncJobActions = <
  Pick<typeof actions, 'create' | 'start' | 'pending' | 'finish' | 'clearFinished'>
>actions;

const AsyncJobSelectors = {
  ...selectors,
  pendingJobs: createSelector(selectors.allEntities, jobStates =>
    jobStates.filter(
      job => job.status === AsyncJobStatus.WORKING || job.status === AsyncJobStatus.START,
    ),
  ),
};

export {
  AsyncJobActions,
  AsyncJobSelectors,
  namespace as AsyncJobNamespace,
  reducer as AsyncJobReducer,
};
